import { useEffect, useState, useCallback } from "react";
import {
  AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { getCreditEconomy, type CreditEconomyStats } from "@/api/adminApi";
import { StatCard } from "@/components/StatCard";
import { RangeSelector } from "@/components/RangeSelector";
import { useRange } from "@/hooks/useRange";

const GOLD = "#D4AF37";
const AMBER = "#C49A2A";

function formatDate(d: string): string {
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const tooltipStyle = {
  contentStyle: { background: "#161616", border: "1px solid #6B5F44", borderRadius: 4 },
  labelStyle: { color: "#9A8E7A", fontSize: 11 },
  itemStyle: { color: "#F0E8D8" },
};

const DIST_LABELS: Record<string, string> = {
  zero: "0",
  "1_50": "1-50",
  "51_250": "51-250",
  "250_plus": "250+",
};

export function CreditsPage() {
  const [rangeDays, setRangeDays] = useRange(30);
  const [stats, setStats] = useState<CreditEconomyStats | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (days: number) => {
    setLoading(true);
    try {
      const data = await getCreditEconomy(days);
      setStats(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(rangeDays); }, [rangeDays, load]);

  const distData = stats
    ? Object.entries(stats.balance_distribution).map(([key, count]) => ({
        name: DIST_LABELS[key] ?? key,
        count,
      }))
    : [];

  return (
    <div>
      <RangeSelector value={rangeDays} onChange={setRangeDays} />

      {loading && <div className="loading">Counting credits...</div>}

      {!loading && stats && (
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Row 1: Burn Rate + Balance Distribution */}
          <div className="grid-2">
            <div className="card chart-card">
              <h3>Credit Burn Rate (Daily)</h3>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={stats.burn_rate_series}>
                  <defs>
                    <linearGradient id="gAmberBurn" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={AMBER} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={AMBER} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#4A454033" />
                  <XAxis dataKey="date" tickFormatter={formatDate} tick={{ fontSize: 10, fill: "#9A8E7A" }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#9A8E7A" }} axisLine={false} tickLine={false} />
                  <Tooltip {...tooltipStyle} labelFormatter={(d: string) => new Date(d).toLocaleDateString()} />
                  <Area type="monotone" dataKey="value" stroke={AMBER} strokeWidth={2} fill="url(#gAmberBurn)" name="Credits Burned" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="card chart-card">
              <h3>Balance Distribution</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={distData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#4A454033" />
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: "#9A8E7A" }} axisLine={false} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#9A8E7A" }} axisLine={false} tickLine={false} />
                  <Tooltip {...tooltipStyle} />
                  <Bar dataKey="count" fill={GOLD} radius={[4, 4, 0, 0]} name="Users" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Row 2: Stat Cards */}
          <div className="grid-3">
            <StatCard
              label="Users at Zero"
              value={stats.zero_balance_count.toString()}
            />
            <StatCard
              label="Converted After Zero"
              value={stats.converted_count.toString()}
              sub={`of ${stats.zero_balance_count} zero-balance users`}
            />
            <StatCard
              label="Conversion Rate"
              value={stats.conversion_after_zero_pct !== null ? `${stats.conversion_after_zero_pct}%` : "N/A"}
              sub="Zero balance → purchase"
            />
          </div>
        </div>
      )}
    </div>
  );
}
